import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { AccOperationalType } from './useData';

export const moduleConfig = {
  module: {
    title: 'Operational types',
    permissionModuleKey: 'acc_operational_types',
    route: {
      list: '/accounting/operational-types',
      new: '/accounting/operational-types/new',
      detail: (id: number) => `/accounting/operational-types/${id}`,
      edit: (id: number) => `/accounting/operational-types/${id}/edit`},
    api: { list: '/acc-operational-types', detail: (id: number) => `/acc-operational-types/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name or code…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccOperationalType[]) => items ,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New operational type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/accounting/operational-types/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccOperationalType) => item.id,
  },
  emptyStates: {
    noData: 'Create operational types for payment pickers (Bank, Cash, AP, AR…).',
    noSearchResults: 'No operational types match your search.',
    noFilterResults: 'No operational types match the filters.',
  },
  delete: {
    title: 'Delete operational type',
    message: 'Delete this operational type? Accounts using it will keep their data but lose the type link.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete operational type. It may be assigned to accounts.',
    handler: async (id: number) => {
      const { deleteAccOperationalType } = await import('../../../services/mutations');
      await deleteAccOperationalType(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Operational type',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This operational type does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Operational type',
    getTitle: (item: AccOperationalType) => (item.name ? item.name : `Type #${item.id}`)},
  form: {
    new: {
      title: 'New operational type',
      subtitle: 'Operational codes (Bank, Cash, AP, AR) used by payment pickers',
      submitText: 'Create'},
    edit: {
      title: 'Edit operational type',
      subtitle: 'Update code, display order, and availability',
      submitText: 'Save'},
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'content' as const,
    formMaxWidth: 'max-w-3xl',
    create: async (data: Partial<AccOperationalType>) => {
      const { createAccOperationalType } = await import('../../../services/mutations');
      return createAccOperationalType(data);
    },
    update: async (id: number, data: Partial<AccOperationalType>) => {
      const { updateAccOperationalType } = await import('../../../services/mutations');
      return updateAccOperationalType(id, data);
    }},
  fields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. BANK, CASH, AP, AR',
      helperText: 'Short code used by operational pickers on journals and payments.',
      validation: { required: true, maxLength: 30 }},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Bank, Cash, Accounts payable',
      helperText: 'Display name shown in operational type selectors.',
      validation: { required: true, maxLength: 100 }},
    {
      name: 'sortOrder',
      label: 'Sort order',
      type: 'number',
      placeholder: '0',
      helperText: 'Lower numbers appear first in operational type dropdowns.',
      defaultValue: 0,
      validation: { min: 0 }},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Available for selection',
      description: 'Inactive types are hidden from pickers but remain on existing accounts.',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Identification',
        description: 'Code and name for this operational classification.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['code', 'name'],
        fieldSpans: {
          code: 'md:col-span-4',
          name: 'md:col-span-8'},
        collapsible: true,
        defaultCollapsed: false},
      {
        id: 'display',
        title: 'Display',
        description: 'Control ordering in selection lists.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['sortOrder'],
        fieldSpans: {
          sortOrder: 'md:col-span-4'},
        collapsible: true,
        defaultCollapsed: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Whether this type can be assigned on new or edited accounts.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive'],
        fieldSpans: {
          isActive: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value != null && value !== '' ? value : '-'}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<AccOperationalType>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => (
      <Badge variant={v ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: AccOperationalType, formatDate: (s?: string) => string) => [
  {
    title: 'Identification',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
    ]},
  {
    title: 'Display & status',
    fields: [
      { label: 'Sort order', value: String(item.sortOrder ?? 0) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const StatusBadges: React.FC<{ item: AccOperationalType }> = ({ item }) => (
  <Badge variant={item.isActive ? 'success' : 'danger'} className="ml-2">
    {item.isActive ? 'Active' : 'Inactive'}
  </Badge>
);

export const getTableActions = createGetTableActions<AccOperationalType>(moduleConfig.module.route);

export const getToolbarIcon = getAccountingToolbarIcon;

export const getStatusBadges = (item: AccOperationalType) => <StatusBadges item={item} />;
export const getInitialFormData = (): Partial<AccOperationalType> => ({
  code: '',
  name: '',
  sortOrder: 0,
  isActive: true});
export const mapItemToFormData = (item: AccOperationalType): Partial<AccOperationalType> => ({
  code: item.code,
  name: item.name,
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true});
